import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../../../applicationUi/components/Table";
import { format } from "date-fns";
import { fetchActiveClientsReport } from "../../../../store/thunks/reportsThunk";
import * as XLSX from "xlsx";
import ClientModal from "../../../../modals/ClientModal";
import { fetchClientById } from "../../../../store/thunks/clientsThunk";
import { toast } from 'react-toastify';

const ActiveClientsReport = () => {
  const dispatch = useDispatch();
  const [daysBack, setDaysBack] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const itemsPerPage = 10;
  const {
    data: clients,
    loading,
    error,
  } = useSelector((state) => state.reports.activeClients || { data: null, loading: false, error: null });

  useEffect(() => {
    dispatch(fetchActiveClientsReport({ daysBack }));
  }, [dispatch, daysBack]);

  const columns = [
    {
      header: "Client Name",
      accessor: "name",
      sortKey: "name",
    },
    {
      header: "Email",
      accessor: "email",
      sortKey: "email",
    },
    {
      header: "Additional Emails",
      accessor: (row) =>
        row.additionalEmails.length > 0
          ? row.additionalEmails.join(", ")
          : "None",
    },
    {
      header: "Last Appointment",
      accessor: (row) =>
        row.lastAppointment
          ? format(new Date(row.lastAppointment), "MM/dd/yyyy")
          : "Never",
      sortKey: "lastAppointment",
    },
    {
      header: "Next Appointment",
      accessor: (row) =>
        row.nextAppointment
          ? format(new Date(row.nextAppointment), "MM/dd/yyyy")
          : "None Scheduled",
      sortKey: "nextAppointment",
    },
    {
      header: "Has Deposit",
      accessor: (row) => (row.hasDeposit ? "Yes" : "No"),
      sortKey: "hasDeposit",
    },
    {
      header: "Deposit Received/Left",
      accessor: (row) => row.depositInfo ? row.depositInfo.display : "-",
    },
    {
      header: "Has Signed Contract",
      accessor: (row) => (row.hasSignedContract ? "Yes" : "No"),
      sortKey: "hasSignedContract",
    },
    {
      header: "Contract Signed Date",
      accessor: (row) =>
        row.contractSignedDate
          ? format(new Date(row.contractSignedDate), "MM/dd/yyyy")
          : "-",
      sortKey: "contractSignedDate",
    },
  ];

  const handleDaysChange = (e) => {
    setDaysBack(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleSortChange = (columnKey) => {
    let direction = 'asc';
    if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: columnKey, direction });
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const handleRowClick = (clientId) => {
    dispatch(fetchClientById(clientId));
    setIsClientModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsClientModalOpen(false);
  };

  // Helper function to get sortable value
  const getSortableValue = (item, key) => {
    switch (key) {
      case 'name':
        return item.name || '';
      case 'email':
        return item.email || '';
      case 'lastAppointment':
        return item.lastAppointment ? new Date(item.lastAppointment) : new Date(0);
      case 'nextAppointment':
        return item.nextAppointment ? new Date(item.nextAppointment) : new Date(0);
      case 'hasDeposit':
        return item.hasDeposit ? 1 : 0;
      case 'hasSignedContract':
        return item.hasSignedContract ? 1 : 0;
      case 'contractSignedDate':
        return item.contractSignedDate ? new Date(item.contractSignedDate) : new Date(0);
      default:
        return '';
    }
  };

  // Sort the clients data
  const sortedClients = useMemo(() => {
    if (!clients || !sortConfig.key) return clients || [];
    
    const sorted = [...clients].sort((a, b) => {
      const aValue = getSortableValue(a, sortConfig.key);
      const bValue = getSortableValue(b, sortConfig.key);
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    return sorted;
  }, [clients, sortConfig]);

  const getDownloadLabel = () => {
    if (daysBack === -1) return "Download Active Clients - All Time";
    if (daysBack === 0) return "Download Active Clients - Future Only";
    return `Download Active Clients - Last ${daysBack} Days`;
  };

  const handleDownload = () => {
    if (!clients || clients.length === 0) {
      toast.warning("No data available to download.");
      return;
    }

    const data = clients.map((client) => ({
      Name: client.name,
      Email: client.email || "N/A",
      "Additional Emails":
        client.additionalEmails.length > 0
          ? client.additionalEmails.join(", ")
          : "None",
      "Last Appointment": client.lastAppointment
        ? format(new Date(client.lastAppointment), "MM/dd/yyyy")
        : "Never",
      "Next Appointment": client.nextAppointment
        ? format(new Date(client.nextAppointment), "MM/dd/yyyy")
        : "None Scheduled",
      "Has Deposit": client.hasDeposit ? "Yes" : "No",
      "Deposit Received/Left": client.depositInfo ? client.depositInfo.display : "-",
      "Has Signed Contract": client.hasSignedContract ? "Yes" : "No",
      "Contract Signed Date": client.contractSignedDate
        ? format(new Date(client.contractSignedDate), "MM/dd/yyyy")
        : "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Active Clients");

    const fileName = `Active_Clients_Report_${
      daysBack === -1 ? "All_Time" : `${daysBack}_Days`
    }.xlsx`;

    XLSX.writeFile(workbook, fileName);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="text-red-500">{typeof error === 'string' ? error : error.message || 'An error occurred'}</div>;

  // Calculate pagination
  const totalItems = Array.isArray(sortedClients) ? sortedClients.length : 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData =
    sortedClients?.slice(startIndex, startIndex + itemsPerPage) || [];

  return (
    <div className="space-y-4">
      {/* Days Back Filter and Download Button */}
      <div className="flex justify-between items-center">
        <div>
          <label
            htmlFor="daysBack"
            className="block text-sm font-medium text-gray-700">
            Show clients with appointments in the last:
          </label>
          <select
            id="daysBack"
            value={daysBack}
            onChange={handleDaysChange}
            className="mt-1 block w-48 rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
            <option value={-1}>All Time</option> {/* New "All Time" option */}
            <option value={0}>Future appointments only</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white shadow-sm rounded-sm">
        <Table
          columns={columns}
          data={paginatedData}
          title="Active Clients Report"
          description="View active clients and their appointment history"
          showPagination={true}
          currentPage={currentPage}
          totalPages={totalPages}
          totalResults={totalItems}
          onPageChange={handlePageChange}
          onSortChange={handleSortChange}
          sortConfig={sortConfig}
          onRowClick={handleRowClick}
          actions={[
            {
              label: getDownloadLabel(),
              onClick: handleDownload,
            },
          ]}
        />
      </div>

      {/* Client Modal */}
      <ClientModal
        isOpen={isClientModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default ActiveClientsReport;